<template>
  <div class="gc-bom-bulk-update-outcome-report">
    <!-- Status header -->
    <div :class="['orpt-header', { 'orpt-header-success': !hasFailures, 'orpt-header-error': hasFailures }]">
      <span class="orpt-header-icon">{{ hasFailures ? '⚠' : '✔' }}</span>
      <span class="orpt-header-title">
        {{ hasFailures ? 'Bulk update applied with errors' : 'Bulk update applied successfully' }}
      </span>
    </div>

    <!-- Metadata row -->
    <div class="orpt-metadata">
      <div class="orpt-metadata-row">
        <span class="orpt-metadata-lbl">Transaction ID</span>
        <span class="orpt-metadata-val orpt-mono">{{ applyResult.TransactionId }}</span>
      </div>
      <div class="orpt-metadata-row">
        <span class="orpt-metadata-lbl">Committed</span>
        <span class="orpt-metadata-val">{{ formatTimestamp(applyResult.DateCommitted) }}</span>
      </div>
      <div class="orpt-metadata-row">
        <span class="orpt-metadata-lbl">Committed by</span>
        <span class="orpt-metadata-val">{{ applyResult.CommittedBy }}</span>
      </div>
    </div>

    <!-- Summary stat cards -->
    <div class="orpt-stats">
      <div class="orpt-stat-card">
        <span class="orpt-stat-label">BOMs updated</span>
        <span class="orpt-stat-val orpt-val-blue">{{ applyResult.BomsUpdatedCount }}</span>
      </div>
      <div class="orpt-stat-card">
        <span class="orpt-stat-label">Failed</span>
        <span :class="['orpt-stat-val', applyResult.Failures.length > 0 ? 'orpt-val-red' : 'orpt-val-muted']">
          {{ applyResult.Failures.length }}
        </span>
      </div>
      <div class="orpt-stat-card">
        <span class="orpt-stat-label">Excluded</span>
        <span :class="['orpt-stat-val', excludedBomIds.length > 0 ? 'orpt-val-red' : 'orpt-val-muted']">
          {{ excludedBomIds.length }}
        </span>
      </div>
    </div>

    <!-- Filter tabs -->
    <div class="orpt-filters">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['orpt-tab', { 'is-active': activeFilter === tab.key }]"
        @click="activeFilter = tab.key"
      >
        <span class="orpt-tab-label">{{ tab.label }}</span>
        <span class="orpt-tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <!-- BOM rows list -->
    <div v-if="filteredRows.length > 0" class="orpt-rows">
      <div v-for="row in filteredRows" :key="row.bomCode" class="orpt-row">
        <div class="orpt-row-main">
          <span :class="['orpt-badge', 'orpt-badge--' + row.status]">
            {{ row.status === 'excluded' ? 'Excluded' : row.status === 'failed' ? 'Failed' : 'Success' }}
          </span>
          <span class="orpt-row-code">{{ row.bomCode }}</span>
          <span class="orpt-row-name">{{ row.productName }}</span>
        </div>
        <div v-if="row.failure" class="orpt-row-detail">
          <div class="orpt-detail-field">
            <span class="orpt-detail-label">Operation:</span>
            <span class="orpt-detail-val">{{ row.failure.OperationAttempted }}</span>
          </div>
          <div class="orpt-detail-field">
            <span class="orpt-detail-label">Reason:</span>
            <span class="orpt-detail-val">{{ row.failure.FailureReason }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="orpt-empty">
      <span>No BOMs in this category</span>
    </div>

    <!-- Success summary message -->
    <div v-if="!hasFailures" class="orpt-success-summary">
      ✓ Bulk update applied successfully
      <br />
      <span class="orpt-summary-details">
        {{ applyResult.BomsUpdatedCount }}/{{ applyResult.BomsIncludedCount }} BOMs updated
        <span v-if="bomsChangedCount"> · {{ bomsChangedCount }} lines changed</span>
        <span v-if="bomsAddedCount"> · {{ bomsAddedCount }} lines added</span>
        · BOM cost recalculated · Ref: {{ applyResult.TransactionId }} · {{ formatTimestamp(applyResult.DateCommitted) }}
      </span>
    </div>

    <!-- Back to list button -->
    <div class="orpt-actions">
      <DxButton
        text="Return to transaction list"
        type="default"
        styling-mode="outlined"
        @click="$emit('back-to-list', true)"
      />
    </div>
  </div>
</template>

<script>
import DxButton from 'devextreme-vue/button';

export default {
  name: 'GcBomBulkUpdateOutcomeReport',
  components: { DxButton },
  props: {
    applyResult: { type: Object, required: true },
    matchedBomList: { type: Array, default: () => [] },
    excludedBomIds: { type: Array, default: () => [] },
    linesChanged: { type: Number, default: 0 },
    linesAdded: { type: Number, default: 0 },
  },
  data() {
    return {
      activeFilter: 'all',
    };
  },
  computed: {
    hasFailures() {
      return this.applyResult.Failures && this.applyResult.Failures.length > 0;
    },
    failureMap() {
      const map = new Map();
      if (this.applyResult.Failures) {
        this.applyResult.Failures.forEach(f => map.set(f.BomCode, f));
      }
      return map;
    },
    excludedSet() {
      return new Set(this.excludedBomIds);
    },
    allRows() {
      return this.matchedBomList.map(bom => ({
        bomCode: bom.BomCode,
        productName: bom.ProductName || '—',
        status: this.excludedSet.has(bom.BomCode)
          ? 'excluded'
          : this.failureMap.has(bom.BomCode)
          ? 'failed'
          : 'success',
        failure: this.failureMap.get(bom.BomCode) || null,
      }));
    },
    successRows() {
      return this.allRows.filter(r => r.status === 'success');
    },
    failedRows() {
      return this.allRows.filter(r => r.status === 'failed');
    },
    excludedRows() {
      return this.allRows.filter(r => r.status === 'excluded');
    },
    filteredRows() {
      switch (this.activeFilter) {
        case 'success':
          return this.successRows;
        case 'failed':
          return this.failedRows;
        case 'excluded':
          return this.excludedRows;
        default:
          return this.allRows;
      }
    },
    tabs() {
      return [
        { key: 'all', label: 'All', count: this.allRows.length },
        { key: 'success', label: 'Success', count: this.successRows.length },
        { key: 'failed', label: 'Failed', count: this.failedRows.length },
        { key: 'excluded', label: 'Excluded', count: this.excludedRows.length },
      ];
    },
    bomsChangedCount() {
      return this.linesChanged;
    },
    bomsAddedCount() {
      return this.linesAdded;
    },
  },
  created() {
    if (this.hasFailures) {
      this.activeFilter = 'failed';
    }
  },
  methods: {
    formatTimestamp(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-outcome-report {
  display: flex;
  flex-direction: column;
  gap: 12px;

  // ── Status header ─────────────────────────────────────────────────────────
  .orpt-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border: 1px solid;
    border-radius: 8px;
    transition: background 0.15s, border-color 0.15s;

    &.orpt-header-success {
      background: #f0fdf4;
      border-color: #86efac;
    }

    &.orpt-header-error {
      background: #fff8f0;
      border-color: #ffd6a5;
    }
  }

  .orpt-header-icon {
    font-size: 20px;
    line-height: 1;

    .orpt-header-success & {
      color: #16a34a;
    }

    .orpt-header-error & {
      color: #ea580c;
    }
  }

  .orpt-header-title {
    font-size: 16px;
    font-weight: 600;

    .orpt-header-success & {
      color: #065f46;
    }

    .orpt-header-error & {
      color: #7c4b00;
    }
  }

  // ── Metadata ──────────────────────────────────────────────────────────────
  .orpt-metadata {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .orpt-metadata-row {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .orpt-metadata-lbl {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
    width: 120px;
    flex-shrink: 0;
  }

  .orpt-metadata-val {
    font-size: 13px;
    color: #101828;
  }

  .orpt-mono {
    font-family: monospace;
    letter-spacing: 0.04em;
  }

  // ── Summary stats ─────────────────────────────────────────────────────────
  .orpt-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .orpt-stat-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .orpt-stat-label {
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .orpt-stat-val {
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
  }

  .orpt-val-blue {
    color: #0369a1;
  }

  .orpt-val-red {
    color: #9b1c1c;
  }

  .orpt-val-muted {
    color: #adb5bd;
  }

  // ── Filter tabs ───────────────────────────────────────────────────────────
  .orpt-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .orpt-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
      border-color: #03a9f4;
      color: #03a9f4;
    }

    &.is-active {
      background: #03a9f4;
      border-color: #03a9f4;
      color: #fff;
    }
  }

  .orpt-tab-label {
    /* inherited */
  }

  .orpt-tab-count {
    font-size: 12px;
    font-weight: 700;
  }

  // ── BOM rows ──────────────────────────────────────────────────────────────
  .orpt-rows {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .orpt-row {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
  }

  .orpt-row-main {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
  }

  .orpt-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    flex-shrink: 0;

    &.orpt-badge--success {
      background: #f0fdf4;
      color: #065f46;
      border: 1px solid #86efac;
    }

    &.orpt-badge--failed {
      background: #fff5f5;
      color: #9b1c1c;
      border: 1px solid #f5c6cb;
    }

    &.orpt-badge--excluded {
      background: #f1f3f5;
      color: #495057;
      border: 1px solid #adb5bd;
    }
  }

  .orpt-row-code {
    font-size: 13px;
    font-weight: 600;
    color: #101828;
  }

  .orpt-row-name {
    font-size: 13px;
    color: #6c757d;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .orpt-row-detail {
    padding: 10px 14px 14px 14px;
    background: #fafbfc;
    border-top: 1px solid #dee2e6;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-left: 38px;
  }

  .orpt-detail-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .orpt-detail-label {
    font-size: 11px;
    font-weight: 600;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.2px;
  }

  .orpt-detail-val {
    font-size: 12px;
    color: #6c757d;
    line-height: 1.4;
    word-break: break-word;
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  .orpt-empty {
    padding: 40px 20px;
    text-align: center;
    color: #6c757d;
    font-size: 14px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  // ── Success summary ────────────────────────────────────────────────────────
  .orpt-success-summary {
    padding: 16px;
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #065f46;
    line-height: 1.5;
  }

  .orpt-summary-details {
    display: block;
    font-size: 13px;
    font-weight: 400;
    color: #3d9b6e;
    margin-top: 4px;
  }

  // ── Actions ───────────────────────────────────────────────────────────────
  .orpt-actions {
    display: flex;
    gap: 10px;
  }
}
</style>
